/**
 * Project Service
 * Supabase 프로젝트 데이터 CRUD 작업
 * FK 관계: projects -> categories -> tasks -> checklists
 */

import { supabase } from '../supabaseClient';

/**
 * 유효한 날짜인지 확인하고, 유효하지 않으면 조정
 * @param {string} dateStr - YYYY-MM-DD 형식의 날짜 문자열
 * @returns {string} 유효한 날짜 문자열
 */
const validateAndFixDate = (dateStr) => {
  if (!dateStr) return null;

  const [year, month, day] = dateStr.split('-').map(Number);

  // 해당 월의 마지막 날짜 계산
  const lastDayOfMonth = new Date(year, month, 0).getDate();

  // day가 해당 월의 마지막 날짜보다 크면 조정
  const validDay = Math.min(day, lastDayOfMonth);

  const validDate = `${year}-${String(month).padStart(2, '0')}-${String(validDay).padStart(2, '0')}`;

  if (validDate !== dateStr) {
    console.warn(`[ProjectService] 날짜 자동 수정: ${dateStr} → ${validDate}`);
  }

  return validDate;
};

/**
 * 사용자 프로필 존재 여부 확인 및 생성
 * @param {string} userId - 사용자 ID
 * @returns {Promise<Object>} 사용자 프로필
 */
const ensureUserProfile = async (userId) => {
  // 프로필 존재 여부 확인
  const { data: existingProfile } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (existingProfile) {
    return existingProfile;
  }

  // 프로필이 없으면 auth.users에서 정보 가져와서 생성
  const { data: { user }, error: userError } = await supabase.auth.getUser();

  if (userError) {
    console.error('[ProjectService] 사용자 정보 조회 실패:', userError);
    throw userError;
  }

  const { data: newProfile, error: createError } = await supabase
    .from('user_profiles')
    .insert({
      id: userId,
      email: user?.email || '',
      updated_display_name: user?.user_metadata?.full_name || user?.email?.split('@')[0] || '사용자',
      updated_avatar_url: user?.user_metadata?.avatar_url || '',
    })
    .select()
    .single();

  if (createError) {
    console.error('[ProjectService] 프로필 생성 실패:', createError);
    throw createError;
  }

  return newProfile;
};

/**
 * 생성된 데이터 롤백 (에러 발생 시)
 * @param {string|null} projectId - 삭제할 프로젝트 ID
 * @param {Array} categoryIds - 삭제할 카테고리 ID 목록
 * @param {Array} taskIds - 삭제할 태스크 ID 목록
 * @param {Array} checklistIds - 삭제할 체크리스트 ID 목록
 */
const rollbackCreatedData = async (projectId, categoryIds = [], taskIds = [], checklistIds = []) => {
  try {
    // 역순으로 삭제: checklists -> tasks -> categories -> project
    if (checklistIds.length > 0) {
      const { error } = await supabase
        .from('checklists')
        .delete()
        .in('id', checklistIds);
      if (error) console.error('[ProjectService] 체크리스트 롤백 실패:', error);
    }

    if (taskIds.length > 0) {
      const { error } = await supabase
        .from('tasks')
        .delete()
        .in('id', taskIds);
      if (error) console.error('[ProjectService] 태스크 롤백 실패:', error);
    }

    if (categoryIds.length > 0) {
      const { error } = await supabase
        .from('categories')
        .delete()
        .in('id', categoryIds);
      if (error) console.error('[ProjectService] 카테고리 롤백 실패:', error);
    }

    if (projectId) {
      // project_members도 함께 삭제
      await supabase
        .from('project_members')
        .delete()
        .eq('project_id', projectId);

      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', projectId);
      if (error) console.error('[ProjectService] 프로젝트 롤백 실패:', error);
    }
  } catch (rollbackError) {
    console.error('[ProjectService] 롤백 중 오류:', rollbackError);
  }
};

/**
 * 프로젝트 생성 (Dify 응답 데이터 저장)
 * FK 순서대로 저장: project -> categories -> tasks -> checklists
 * 에러 발생 시 자동 롤백
 *
 * @param {Object} data - Dify에서 변환된 데이터
 * @param {string} userId - 생성자 ID
 * @returns {Promise<Object>} 생성된 프로젝트 (전체 구조 포함)
 */
export const createProject = async (data, userId) => {
  const { project, categories } = data;

  // 롤백용 ID 추적
  let createdProjectId = null;
  const createdCategoryIds = [];
  const createdTaskIds = [];
  const createdChecklistIds = [];

  try {
    // 0. 사용자 프로필 확인/생성
    await ensureUserProfile(userId);
    const { data: createdProject, error: projectError } = await supabase
      .from('projects')
      .insert({
        owner_id: userId,
        name: project.name,
        description: project.description,
        start_date: project.start_date,
        end_date: project.end_date,
        ai_estimated_cost: project.ai_estimated_cost,
        ai_estimated_cost_reasons: project.ai_estimated_cost_reasons,
      })
      .select()
      .single();

    if (projectError) {
      console.error('[ProjectService] 프로젝트 생성 실패:', projectError);
      throw projectError;
    }

    createdProjectId = createdProject.id;
    const categoriesWithIds = [];

    for (const category of categories) {
      const { data: createdCategory, error: categoryError } = await supabase
        .from('categories')
        .insert({
          project_id: createdProject.id,
          name: category.name,
          sort_order: category.sort_order,
          created_by: userId,
        })
        .select()
        .single();

      if (categoryError) {
        console.error('[ProjectService] 카테고리 생성 실패:', categoryError);
        throw categoryError;
      }

      createdCategoryIds.push(createdCategory.id);

      // 3. 태스크 생성
      const tasksWithIds = [];
      for (const task of category.tasks) {
        const { data: createdTask, error: taskError } = await supabase
          .from('tasks')
          .insert({
            category_id: createdCategory.id,
            name: task.name,
            description: task.description,
            start_date: validateAndFixDate(task.start_date),
            end_date: validateAndFixDate(task.end_date),
            created_by: userId,
          })
          .select()
          .single();

        if (taskError) {
          console.error('[ProjectService] 태스크 생성 실패:', taskError);
          console.error('[ProjectService] 실패한 태스크 데이터:', {
            category_id: createdCategory.id,
            name: task.name,
            description: task.description,
            start_date: task.start_date,
            start_date_fixed: validateAndFixDate(task.start_date),
            end_date: task.end_date,
            end_date_fixed: validateAndFixDate(task.end_date),
          });
          throw taskError;
        }

        createdTaskIds.push(createdTask.id);

        // 4. 체크리스트 생성
        const checklistsWithIds = [];
        for (const checklist of task.checklists) {
          const { data: createdChecklist, error: checklistError } = await supabase
            .from('checklists')
            .insert({
              task_id: createdTask.id,
              name: checklist.name,
              hint: checklist.hint || null,
              is_completed: checklist.is_completed || false,
              created_by: userId,
            })
            .select()
            .single();

          if (checklistError) {
            console.error('[ProjectService] 체크리스트 생성 실패:', checklistError);
            console.error('[ProjectService] 실패한 체크리스트 데이터:', {
              task_id: createdTask.id,
              name: checklist.name,
              hint: checklist.hint,
              is_completed: checklist.is_completed,
            });
            throw checklistError;
          }

          createdChecklistIds.push(createdChecklist.id);
          checklistsWithIds.push(createdChecklist);
        }

        tasksWithIds.push({
          ...createdTask,
          checklists: checklistsWithIds,
        });
      }

      categoriesWithIds.push({
        ...createdCategory,
        tasks: tasksWithIds,
      });
    }

    // 5. 프로젝트 멤버 추가 (owner)
    const { error: memberError } = await supabase
      .from('project_members')
      .insert({
        project_id: createdProject.id,
        user_id: userId,
        role: '관리자',
      });

    if (memberError) {
      console.error('[ProjectService] 멤버 추가 실패:', memberError);
    }

    return {
      ...createdProject,
      categories: categoriesWithIds,
    };

  } catch (error) {
    console.error('[ProjectService] 프로젝트 생성 중 오류:', error);

    // 롤백 실행
    await rollbackCreatedData(
      createdProjectId,
      createdCategoryIds,
      createdTaskIds,
      createdChecklistIds
    );

    throw error;
  }
};

/** ISO 문자열 또는 Date → YYYY-MM-DD */
const toISODateOnly = (d) => {
  if (d == null) return null;
  if (typeof d === 'string') return d.split('T')[0];
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return null;
  return x.toISOString().split('T')[0];
};

/** 오늘(로컬) YYYY-MM-DD */
const todayLocalISO = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** 캘린더 일 수 차이 (to - from) */
const diffCalendarDays = (fromIso, toIso) => {
  const [y1, m1, d1] = fromIso.split('-').map(Number);
  const [y2, m2, d2] = toIso.split('-').map(Number);
  const u1 = Date.UTC(y1, m1 - 1, d1);
  const u2 = Date.UTC(y2, m2 - 1, d2);
  return Math.round((u2 - u1) / 86400000);
};

const addDaysToISODate = (iso, days) => {
  const [y, m, d] = iso.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + days);
  const yy = dt.getUTCFullYear();
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

/**
 * 커뮤니티 템플릿 프로젝트를 복사해 새 프로젝트 생성 (posts/chat 제외)
 * @param {{ templateProjectId: string, userId: string, anchorDateISO?: string|null }} opts
 * @returns {Promise<{ newProjectId: string }>}
 */
export const createProjectFromCommunityTemplate = async ({
  templateProjectId,
  userId,
  anchorDateISO,
}) => {
  await ensureUserProfile(userId);

  const { data: srcProject, error: pErr } = await supabase
    .from('projects')
    .select(
      'id, name, description, start_date, end_date, cost_unit, ai_estimated_cost, ai_estimated_cost_reasons, updated_cost'
    )
    .eq('id', templateProjectId)
    .eq('is_template', true)
    .maybeSingle();

  if (pErr) throw pErr;
  if (!srcProject) {
    throw new Error('템플릿을 찾을 수 없습니다.');
  }

  const { data: categories, error: cErr } = await supabase
    .from('categories')
    .select('id, name, sort_order, created_by')
    .eq('project_id', templateProjectId)
    .order('sort_order', { ascending: true });

  if (cErr) throw cErr;
  if (!categories?.length) {
    throw new Error('템플릿에 카테고리가 없습니다.');
  }

  const catIds = categories.map((c) => c.id);
  const { data: tasks, error: tErr } = await supabase
    .from('tasks')
    .select('id, category_id, name, description, start_date, end_date, created_by, created_at')
    .in('category_id', catIds)
    .order('created_at', { ascending: true });

  if (tErr) throw tErr;
  if (!tasks?.length) {
    throw new Error('템플릿에 작업이 없습니다.');
  }

  const taskIds = tasks.map((t) => t.id);
  const { data: checklists, error: clErr } = await supabase
    .from('checklists')
    .select('id, task_id, name, hint, created_by, created_at')
    .in('task_id', taskIds)
    .order('created_at', { ascending: true });

  if (clErr) throw clErr;

  let minStart = null;
  let maxEnd = null;
  for (const t of tasks) {
    const s = validateAndFixDate(toISODateOnly(t.start_date));
    const e = validateAndFixDate(toISODateOnly(t.end_date));
    if (s) {
      if (!minStart || s < minStart) minStart = s;
    }
    if (e) {
      if (!maxEnd || e > maxEnd) maxEnd = e;
    }
  }
  if (!minStart || !maxEnd) {
    throw new Error('템플릿 작업에 유효한 시작일·종료일이 없습니다.');
  }

  const anchor = anchorDateISO && String(anchorDateISO).trim() ? String(anchorDateISO).trim() : todayLocalISO();
  const offsetDays = diffCalendarDays(minStart, anchor);

  const shift = (iso) => {
    const base = validateAndFixDate(toISODateOnly(iso));
    if (!base) return null;
    return validateAndFixDate(addDaysToISODate(base, offsetDays));
  };

  const projectStart = anchor;
  const projectEnd = shift(maxEnd);
  if (!projectEnd) {
    throw new Error('프로젝트 종료일을 계산할 수 없습니다.');
  }

  let createdProjectId = null;
  const createdCategoryIds = [];
  const createdTaskIds = [];
  const createdChecklistIds = [];

  try {
    const nowIso = new Date().toISOString();

    const { data: newProject, error: insPErr } = await supabase
      .from('projects')
      .insert({
        owner_id: userId,
        name: srcProject.name,
        description: srcProject.description ?? '',
        start_date: projectStart,
        end_date: projectEnd,
        cost_unit: srcProject.cost_unit || '원',
        ai_estimated_cost: srcProject.ai_estimated_cost,
        ai_estimated_cost_reasons: srcProject.ai_estimated_cost_reasons,
        updated_cost: srcProject.updated_cost,
        is_template: false,
        template_desc: null,
        template_category_id: null,
        updated_at: nowIso,
        updated_by: userId,
      })
      .select()
      .single();

    if (insPErr) throw insPErr;
    createdProjectId = newProject.id;

    const tasksByCategory = {};
    for (const t of tasks) {
      if (!tasksByCategory[t.category_id]) tasksByCategory[t.category_id] = [];
      tasksByCategory[t.category_id].push(t);
    }

    const clByTask = {};
    for (const cl of checklists || []) {
      if (!clByTask[cl.task_id]) clByTask[cl.task_id] = [];
      clByTask[cl.task_id].push(cl);
    }

    for (const cat of categories) {
      const { data: newCat, error: catErr } = await supabase
        .from('categories')
        .insert({
          project_id: createdProjectId,
          name: cat.name,
          sort_order: cat.sort_order,
          created_by: userId,
          updated_at: nowIso,
          updated_by: userId,
        })
        .select()
        .single();

      if (catErr) throw catErr;
      createdCategoryIds.push(newCat.id);

      const catTasks = tasksByCategory[cat.id] || [];
      for (const task of catTasks) {
        const ns = shift(toISODateOnly(task.start_date));
        const ne = shift(toISODateOnly(task.end_date));
        if (!ns || !ne) {
          throw new Error(`작업 "${task.name}"의 날짜를 옮길 수 없습니다.`);
        }

        const { data: newTask, error: taskErr } = await supabase
          .from('tasks')
          .insert({
            category_id: newCat.id,
            name: task.name,
            description: task.description ?? '',
            start_date: ns,
            end_date: ne,
            created_by: userId,
            updated_at: nowIso,
            updated_by: userId,
          })
          .select()
          .single();

        if (taskErr) throw taskErr;
        createdTaskIds.push(newTask.id);

        const taskChecklists = clByTask[task.id] || [];
        for (const cl of taskChecklists) {
          const { data: newCl, error: clInsErr } = await supabase
            .from('checklists')
            .insert({
              task_id: newTask.id,
              name: cl.name,
              hint: cl.hint || null,
              is_completed: false,
              completed_at: null,
              completed_by: null,
              created_by: userId,
              updated_at: nowIso,
              updated_by: userId,
            })
            .select()
            .single();

          if (clInsErr) throw clInsErr;
          createdChecklistIds.push(newCl.id);
        }
      }
    }

    const { error: memErr } = await supabase.from('project_members').insert({
      project_id: createdProjectId,
      user_id: userId,
      role: '관리자',
    });
    if (memErr) throw memErr;

    const { error: usageErr } = await supabase.from('project_usage').insert({
      user_id: userId,
      project_id: templateProjectId,
    });
    if (usageErr) throw usageErr;

    return { newProjectId: createdProjectId };
  } catch (error) {
    console.error('[ProjectService] 커뮤니티 템플릿 복사 실패:', error);
    await rollbackCreatedData(
      createdProjectId,
      createdCategoryIds,
      createdTaskIds,
      createdChecklistIds
    );
    throw error;
  }
};

/**
 * 사용자의 프로젝트 목록 조회
 * @param {string} userId - 사용자 ID
 * @returns {Promise<Array>} 프로젝트 목록
 */
export const getProjects = async (userId) => {
  try {
    // 사용자가 멤버로 있는 프로젝트 조회
    const { data: memberProjects, error: memberError } = await supabase
      .from('project_members')
      .select('project_id')
      .eq('user_id', userId)
      .is('left_at', null)
      .is('kicked_at', null);

    if (memberError) throw memberError;

    const projectIds = memberProjects.map(m => m.project_id);

    if (projectIds.length === 0) {
      return [];
    }

    // 프로젝트 상세 정보 조회
    const { data: projects, error: projectError } = await supabase
      .from('projects')
      .select('*')
      .in('id', projectIds)
      .order('created_at', { ascending: false });

    if (projectError) throw projectError;

    return projects;

  } catch (error) {
    console.error('[ProjectService] 프로젝트 목록 조회 실패:', error);
    throw error;
  }
};

/**
 * 프로젝트 상세 조회 (전체 구조 포함)
 * @param {string} projectId - 프로젝트 ID
 * @returns {Promise<Object>} 프로젝트 전체 데이터
 */
export const getProjectDetail = async (projectId) => {
  try {
    // 프로젝트 기본 정보
    const { data: project, error: projectError } = await supabase
      .from('projects')
      .select('*')
      .eq('id', projectId)
      .single();

    if (projectError) throw projectError;

    // 카테고리 조회
    const { data: categories, error: categoryError } = await supabase
      .from('categories')
      .select('*')
      .eq('project_id', projectId)
      .order('sort_order', { ascending: true });

    if (categoryError) throw categoryError;

    // 각 카테고리의 태스크와 체크리스트 조회
    const categoriesWithTasks = await Promise.all(
      categories.map(async (category) => {
        const { data: tasks, error: taskError } = await supabase
          .from('tasks')
          .select('*')
          .eq('category_id', category.id)
          .order('start_date', { ascending: true });

        if (taskError) throw taskError;

        const tasksWithChecklists = await Promise.all(
          tasks.map(async (task) => {
            // 체크리스트 조회
            const { data: checklists, error: checklistError } = await supabase
              .from('checklists')
              .select('*')
              .eq('task_id', task.id)
              .order('created_at', { ascending: true });

            if (checklistError) throw checklistError;

            // 담당자 조회
            const { data: assignees, error: assigneeError } = await supabase
              .from('task_assignees')
              .select(`
                user_id,
                user_profiles (
                  id,
                  updated_display_name,
                  updated_avatar_url
                )
              `)
              .eq('task_id', task.id);

            if (assigneeError) throw assigneeError;

            // 포스트 조회
            const { data: posts, error: postError } = await supabase
              .from('posts')
              .select(`
                *,
                author:user_profiles (
                  id,
                  updated_display_name,
                  updated_avatar_url
                )
              `)
              .eq('task_id', task.id)
              .order('created_at', { ascending: false });

            if (postError) throw postError;

            // 각 포스트의 댓글 조회
            const postsWithComments = await Promise.all(
              posts.map(async (post) => {
                const { data: comments, error: commentError } = await supabase
                  .from('comments')
                  .select(`
                    *,
                    author:user_profiles (
                      id,
                      updated_display_name,
                      updated_avatar_url
                    )
                  `)
                  .eq('post_id', post.id)
                  .order('created_at', { ascending: false });

                if (commentError) throw commentError;

                return { ...post, comments };
              })
            );

            return {
              ...task,
              checklists,
              assignees: assignees.map(a => a.user_profiles),
              posts: postsWithComments,
            };
          })
        );

        return {
          ...category,
          tasks: tasksWithChecklists,
        };
      })
    );

    // 프로젝트 멤버 조회
    const { data: members, error: memberError } = await supabase
      .from('project_members')
      .select(`
        role,
        joined_at,
        user_profiles (
          id,
          updated_display_name,
          updated_avatar_url,
          email
        )
      `)
      .eq('project_id', projectId)
      .is('left_at', null)
      .is('kicked_at', null);

    if (memberError) throw memberError;

    return {
      ...project,
      categories: categoriesWithTasks,
      members: members.map(m => ({
        ...m.user_profiles,
        role: m.role,
        joined_at: m.joined_at,
      })),
    };

  } catch (error) {
    console.error('[ProjectService] 프로젝트 상세 조회 실패:', error);
    throw error;
  }
};

/**
 * 체크리스트 완료 상태 토글
 * @param {string} checklistId - 체크리스트 ID
 * @param {boolean} isCompleted - 완료 상태
 * @param {string} userId - 사용자 ID
 */
export const toggleChecklist = async (checklistId, isCompleted, userId) => {
  try {
    const { data, error } = await supabase
      .from('checklists')
      .update({
        is_completed: isCompleted,
        completed_at: isCompleted ? new Date().toISOString() : null,
        completed_by: isCompleted ? userId : null,
        updated_at: new Date().toISOString(),
        updated_by: userId,
      })
      .eq('id', checklistId)
      .select()
      .single();

    if (error) throw error;
    return data;

  } catch (error) {
    console.error('[ProjectService] 체크리스트 토글 실패:', error);
    throw error;
  }
};

/**
 * 포스트 생성
 * @param {string} taskId - 태스크 ID
 * @param {string} content - 포스트 내용
 * @param {string} userId - 작성자 ID
 * @param {Array} files - 첨부 파일 (선택)
 */
export const createPost = async (taskId, content, userId, files = []) => {
  try {
    const { data, error } = await supabase
      .from('posts')
      .insert({
        task_id: taskId,
        author_id: userId,
        content,
        files,
      })
      .select(`
        *,
        author:user_profiles (
          id,
          updated_display_name,
          updated_avatar_url
        )
      `)
      .single();

    if (error) throw error;
    return { ...data, comments: [] };

  } catch (error) {
    console.error('[ProjectService] 포스트 생성 실패:', error);
    throw error;
  }
};

/**
 * 댓글 생성
 * @param {string} postId - 포스트 ID
 * @param {string} content - 댓글 내용
 * @param {string} userId - 작성자 ID
 */
export const createComment = async (postId, content, userId) => {
  try {
    const { data, error } = await supabase
      .from('comments')
      .insert({
        post_id: postId,
        author_id: userId,
        content,
      })
      .select(`
        *,
        author:user_profiles (
          id,
          updated_display_name,
          updated_avatar_url
        )
      `)
      .single();

    if (error) throw error;
    return data;

  } catch (error) {
    console.error('[ProjectService] 댓글 생성 실패:', error);
    throw error;
  }
};

/**
 * 프로젝트 삭제
 * @param {string} projectId - 프로젝트 ID
 */
export const deleteProject = async (projectId) => {
  try {
    // CASCADE로 관련 데이터 모두 삭제됨
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId);

    if (error) throw error;

  } catch (error) {
    console.error('[ProjectService] 프로젝트 삭제 실패:', error);
    throw error;
  }
};

/**
 * 채팅 메시지 전송
 * @param {string} projectId - 프로젝트 ID
 * @param {string} message - 메시지 내용
 * @param {string} userId - 작성자 ID
 * @param {Array} files - 첨부 파일
 */
export const sendChatMessage = async (projectId, message, userId, files = []) => {
  try {
    const { data, error } = await supabase
      .from('chat_messages')
      .insert({
        project_id: projectId,
        author_id: userId,
        message,
        files,
      })
      .select(`
        *,
        author:user_profiles (
          id,
          updated_display_name,
          updated_avatar_url
        )
      `)
      .single();

    if (error) throw error;
    return data;

  } catch (error) {
    console.error('[ProjectService] 채팅 메시지 전송 실패:', error);
    throw error;
  }
};

/**
 * 채팅 메시지 조회
 * @param {string} projectId - 프로젝트 ID
 * @param {number} limit - 조회 개수
 */
export const getChatMessages = async (projectId, limit = 50) => {
  try {
    const { data, error } = await supabase
      .from('chat_messages')
      .select(`
        *,
        author:user_profiles (
          id,
          updated_display_name,
          updated_avatar_url
        )
      `)
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data.reverse(); // 오래된 순으로 정렬

  } catch (error) {
    console.error('[ProjectService] 채팅 메시지 조회 실패:', error);
    throw error;
  }
};

const projectService = {
  createProject,
  createProjectFromCommunityTemplate,
  getProjects,
  getProjectDetail,
  toggleChecklist,
  createPost,
  createComment,
  deleteProject,
  sendChatMessage,
  getChatMessages,
};

export default projectService;
